import type { CatalogOption, WSEventData } from '../../types/entities';
import type { BaseAction } from '../../types/redux';
import {
  QE_CATALOG_ERROR,
  QE_CATALOG_LOADING,
  QE_ERROR,
  QE_LOADING,
  QE_QUERY_FILTER_STATE,
  QE_RESET_QUERY_STATE,
  QE_RESET_REF_ID,
  QE_SET_ACTIVE_INDEX,
  // Catalog actions moved from catalog reducer
  QE_SET_CATALOG,
  QE_SET_CONTINUE_ON_ERROR,
  QE_SET_DELTA,
  QE_SET_QHISTORY_DATA,
  QE_SET_QUERY_HISTORY_FILTER,
  QE_SET_QUERY_HISTORY_SEARCH,
  QE_SET_SYSTEM_CATALOG_FLAG,
  QE_SET_WS_DISABLED_MESSAGE,
  QE_SHEET_FETCH_ERROR,
  QE_UPDATE_STATE,
  QUERY_TEXT,
} from './types';

export interface QELoadingAction extends BaseAction {
  type: typeof QE_LOADING;
  loading: boolean;
}

export interface QEErrorAction extends BaseAction {
  type: typeof QE_ERROR;
  data: unknown;
}

export interface QESheetFetchErrorAction extends BaseAction {
  type: typeof QE_SHEET_FETCH_ERROR;
  error: string | null;
}

export interface SetActiveTabAction extends BaseAction {
  type: typeof QE_SET_ACTIVE_INDEX;
  index: number;
}

export interface HandleQEUpdateAction extends BaseAction {
  type: typeof QE_UPDATE_STATE;
  key: string;
  id: string;
  data: Record<string, unknown>;
  deltaID: string;
  eventObj: WSEventData;
}

export interface ResetQueryStateAction extends BaseAction {
  type: typeof QE_RESET_QUERY_STATE;
}

export interface SetQHistoryFilterAction extends BaseAction {
  type: typeof QE_SET_QUERY_HISTORY_FILTER;
  key: string;
  value: unknown;
}

export interface SetQHistorySearchAction extends BaseAction {
  type: typeof QE_SET_QUERY_HISTORY_SEARCH;
  updatedObj: Record<string, unknown>;
}

export interface SetDeltaQEAction extends BaseAction {
  type: typeof QE_SET_DELTA;
  tabID: string;
  val: boolean;
}

export interface SetQHistoryDataAction extends BaseAction {
  type: typeof QE_SET_QHISTORY_DATA;
  data: Record<string, unknown>;
}

export interface SetWSDisabledAction extends BaseAction {
  type: typeof QE_SET_WS_DISABLED_MESSAGE;
}

export interface ResetRefIDAction extends BaseAction {
  type: typeof QE_RESET_REF_ID;
  ref_id: string;
  key: string;
}

export interface SetCatalogAction extends BaseAction {
  type: typeof QE_SET_CATALOG;
  data: CatalogOption[];
}

export interface CatalogLoadingAction extends BaseAction {
  type: typeof QE_CATALOG_LOADING;
  loading: boolean;
}

export interface CatalogErrorAction extends BaseAction {
  type: typeof QE_CATALOG_ERROR;
  data: unknown;
}

export interface EditorQueriesReduxAction extends BaseAction {
  type: typeof QUERY_TEXT;
  ref_id: string;
  value: Record<string, unknown>;
}

export interface SetQueryFilterStateAction extends BaseAction {
  type: typeof QE_QUERY_FILTER_STATE;
  tab_id: string;
  value: {
    catalog?: string;
    database?: string;
    cluster?: string;
  };
}

export type EditorAction =
  | QELoadingAction
  | QEErrorAction
  | QESheetFetchErrorAction
  | SetActiveTabAction
  | HandleQEUpdateAction
  | ResetQueryStateAction
  | SetQHistoryFilterAction
  | SetQHistorySearchAction
  | SetDeltaQEAction
  | SetQHistoryDataAction
  | SetWSDisabledAction
  | ResetRefIDAction
  | SetCatalogAction
  | CatalogLoadingAction
  | CatalogErrorAction
  | EditorQueriesReduxAction
  | SetQueryFilterStateAction
  | SetSystemCatalogFlagAction
  | SetContinueOnErrorAction;

export function loading(isLoading: boolean): QELoadingAction {
  return {
    type: QE_LOADING,
    loading: isLoading,
  };
}

export function error(data: unknown): QEErrorAction {
  return {
    type: QE_ERROR,
    data: data,
  };
}

export function sheetFetchError(error: string | null): QESheetFetchErrorAction {
  return {
    type: QE_SHEET_FETCH_ERROR,
    error,
  };
}

export const setActiveTab = (index: number): SetActiveTabAction => {
  return {
    type: QE_SET_ACTIVE_INDEX,
    index,
  };
};

export const handleQEUpdate = (
  key: string,
  data: Record<string, unknown> | unknown[],
  id: string = '',
  deltaID: string = '',
  eventObj: WSEventData = {}
): HandleQEUpdateAction => {
  return {
    type: QE_UPDATE_STATE,
    key,
    id,
    data: data as Record<string, unknown>,
    deltaID,
    eventObj,
  };
};

export function resetQuerySavedState(): ResetQueryStateAction {
  return {
    type: QE_RESET_QUERY_STATE,
  };
}

export function setqHistoryFilter(key: string, value: unknown): SetQHistoryFilterAction {
  return {
    type: QE_SET_QUERY_HISTORY_FILTER,
    key,
    value,
  };
}

export function setqHistorySearch(updatedObj: Record<string, unknown>): SetQHistorySearchAction {
  return {
    type: QE_SET_QUERY_HISTORY_SEARCH,
    updatedObj,
  };
}

export function setDeltaQE(tabID: string, val: boolean): SetDeltaQEAction {
  return {
    type: QE_SET_DELTA,
    tabID,
    val,
  };
}

export function setQHistoryData(data: Record<string, unknown>): SetQHistoryDataAction {
  return {
    type: QE_SET_QHISTORY_DATA,
    data,
  };
}

export function setWSDisabledForRunningQueries(): SetWSDisabledAction {
  return {
    type: QE_SET_WS_DISABLED_MESSAGE,
  };
}

export function resetRefID(ref_id: string, key: string = ''): ResetRefIDAction {
  return {
    type: QE_RESET_REF_ID,
    ref_id,
    key,
  };
}

// Catalog action creators (moved from catalog reducer)
export function setCatalog(data: CatalogOption[]): SetCatalogAction {
  return {
    type: QE_SET_CATALOG,
    data: data,
  };
}

export function catalogLoading(isLoading: boolean): CatalogLoadingAction {
  return {
    type: QE_CATALOG_LOADING,
    loading: isLoading,
  };
}

export function catalogError(data: unknown): CatalogErrorAction {
  return {
    type: QE_CATALOG_ERROR,
    data: data,
  };
}

export function editorQueriesRedux(
  ref_id: string,
  value: Record<string, unknown>
): EditorQueriesReduxAction {
  return {
    type: QUERY_TEXT,
    ref_id,
    value,
  };
}

export interface SetSystemCatalogFlagAction extends BaseAction {
  type: typeof QE_SET_SYSTEM_CATALOG_FLAG;
  enabled: boolean;
  tables?: string[];
}

export function setSystemCatalogFlag(
  enabled: boolean,
  tables?: string[]
): SetSystemCatalogFlagAction {
  return {
    type: QE_SET_SYSTEM_CATALOG_FLAG,
    enabled,
    tables,
  };
}

export interface SetContinueOnErrorAction extends BaseAction {
  type: typeof QE_SET_CONTINUE_ON_ERROR;
  value: boolean;
}

/**
 * Set the user-level "continue on error" preference. One value for the whole
 * editor, so every open query sheet reflects the change immediately.
 */
export function setContinueOnErrorFlag(value: boolean): SetContinueOnErrorAction {
  return {
    type: QE_SET_CONTINUE_ON_ERROR,
    value,
  };
}

export function setQueryFilterStateEditor(
  tab_id: string,
  value: { catalog?: string; database?: string; cluster?: string }
): SetQueryFilterStateAction {
  return {
    type: QE_QUERY_FILTER_STATE,
    tab_id,
    value,
  };
}
